import React from "react";
import { Container, Header, Segment, List, Message } from "semantic-ui-react";

const DarInvenioCliPage = () => {
  const codeBlockStyle = {
    whiteSpace: "pre",
    fontFamily: "monospace",
    overflowX: "auto",
  };

  return (
    <Container>
      <Segment padded>
        <Header as="h1">DAR-INVENIO-CLI</Header>
        <p>
          DAR-INVENIO-CLI is a command-line tool for creating drafts and
          uploading files in the DAR Invenio webpage.
        </p>
      </Segment>

      <Header as="h2" attached="top">
        Installation
      </Header>
      <Segment attached>
        <Header as="h3">From PyPI (Recommended)</Header>

        <p>This installation may vary for different operating systems.</p>
        <p>
          For that case we provide a link to
          <a
            style={{ padding: 0, margin: 0, color: "#4183C4" }}
            href="https://pypi.org/project/dar-invenio-cli/"
          >
            {" PyPI package"}
          </a>
        </p>
        <Header as="h4">Steps with venv activation:</Header>
        <Message style={codeBlockStyle}>sudo apt install python3-full</Message>
        <Message style={codeBlockStyle}>python3 -m venv venv</Message>
        <Message style={codeBlockStyle}>source venv/bin/activate</Message>
        <Message style={codeBlockStyle}>pip install dar-invenio-cli</Message>
      </Segment>
      <Segment attached>
        <Header as="h3">From source (cloned repository)</Header>
        <List ordered>
          <List.Item>
            <Header as="h5">Install Python and venv:</Header>
            <Message style={codeBlockStyle}>
              {`sudo apt update
sudo apt install python3 python3-pip python3-venv git`}
            </Message>
          </List.Item>
          <List.Item>
            <Header as="h5">Clone the repository:</Header>
            <Message style={codeBlockStyle}>
              {`git clone git@gitlab.ics.muni.cz:dataraptors/elter/dar-invenio-cli.git
cd dar-invenio-cli`}
            </Message>
          </List.Item>
          <List.Item>
            <Header as="h5">Create and activate a virtual environment:</Header>
            <Message style={codeBlockStyle}>
              {`python3 -m venv .venv
source .venv/bin/activate`}
            </Message>
          </List.Item>
          <List.Item>
            <Header as="h5">Install the package:</Header>
            <Message style={codeBlockStyle}>pip install .</Message>
          </List.Item>
        </List>
      </Segment>

      <Header as="h2" attached="top">
        Configuration
      </Header>
      <Segment attached>
        <p>
          Before using the features, you need to set up the configuration. The
          configuration initialization with `api_token` is required. Values for
          `base-api-url`, `model` and `community` can be passed as optional,
          otherwise default values will be used.
        </p>
        <Message style={codeBlockStyle}>
          dar-invenio-cli config init --api-token &lt;your-api-token&gt
          --base-api-url &lt;your-base-api-url&gt; --model &lt;your-model&gt
          --community &lt;your-community&gt;
        </Message>
        <p>
          You can also update the values for `base-api-url` and `model` or
          `api_token` later:
        </p>
        <Message style={codeBlockStyle}>dar-invenio-cli config update</Message>
        <p>with optional options:</p>
        <List bulleted>
          <List.Item>--base-api-url &lt;your-base-api-url&gt;</List.Item>
          <List.Item>--model &lt;your-model&gt;</List.Item>
          <List.Item>--api-token &lt;your-api-token&gt;</List.Item>
        </List>
        <p>
          The init script sets default values for the cli and you can see the
          actual configuration by running:
        </p>
        <Message style={codeBlockStyle}>dar-invenio-cli config show</Message>
      </Segment>

      <Header as="h2" attached="top">
        Usage
      </Header>
      <Segment attached>
        <p>
          If you do not have configuration set up, the commands will prompt you
          to run the configuration initialization first.
        </p>
        <Header as="h3">Create a single draft</Header>
        <p>
          From a JSON file - you need to provide valid json with its metadata
          structure, otherwise the creation may fail:
        </p>
        <Message style={codeBlockStyle}>
          dar-invenio-cli create draft --from-file "/path/to/your/file.json"
        </Message>
        <p>
          From a name - creates a draft with empty metadata except for the
          title:
        </p>
        <Message style={codeBlockStyle}>
          dar-invenio-cli create draft --from-title "My Draft Name"
        </Message>

        <Header as="h3">Create multiple drafts</Header>
        <p>From multiple JSON files:</p>
        <Message style={codeBlockStyle}>
          dar-invenio-cli create drafts --from-files "/path/to/file1.json"
          --from-files "/path/to/file2.json"
        </Message>
        <p>From a folder containing JSON files:</p>
        <Message style={codeBlockStyle}>
          dar-invenio-cli create drafts --from-folder "/path/to/your/folder"
        </Message>

        <Header as="h3">Upload files to a draft</Header>
        <Message style={codeBlockStyle}>
          dar-invenio-cli upload files &lt;draft_id&gt; "/path/to/file1"
          "/path/to/file2"
        </Message>

        <Header as="h3">Upload files from a folder to a draft</Header>
        <p>
          It uploads all files from the specified folder to the given draft - if
          you want to have it in one file, create and upload ZIP.
        </p>
        <Message style={codeBlockStyle}>
          dar-invenio-cli upload folder &lt;draft_id&gt; "/path/to/your/folder"
        </Message>
      </Segment>
      <Header as="h2" attached="top">
        Examples
      </Header>
      <Segment attached>
        <p>
          Examples of usage and JSON Create examples can be found in the{" "}
          <a href="https://github.com/eLTER-RI/dar-cli/tree/main/dar-cli-commands">
            eLTER Github Pages.
          </a>
        </p>
      </Segment>
      <Header as="h2" attached="top">
        For Developers
      </Header>
      <Segment attached>
        <p>
          The core logic is located in `dar_invenio_cli/core.py`. You can import
          and use the functions from this module in your own Python projects.
        </p>
      </Segment>
    </Container>
  );
};

export default DarInvenioCliPage;
